import * as _ from 'underscore';

import * as React from 'react';

import { MenuItemInfo } from '@tableau/widgets';

import { defaultMemoize } from 'reselect/lib';
import { Messages } from '../../../codegen/Localize';
import { ButtonControlledChildComponentProps, ButtonControlledComponent, ButtonProps } from '../../../common/react/components/ButtonControlledComponent';
import { ButtonControlledFlyoutListMenu, ButtonControlledFlyoutListMenuProps } from '../../../common/react/components/ButtonControlledFlyoutListMenu';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { ExplorerDropdownButton } from '../Components/ExplorerDropdownButton';

export type DatasourceViewType = 'datasource' | 'dataconnection';

export interface DatasourceViewTypeInfo {
  displayValue: () => string;
  type: ExploreContentApi_ContentType;
}

const ViewTypeDictionary: { [V in DatasourceViewType]: DatasourceViewTypeInfo} = {
  'datasource' : {
    displayValue: Messages.dataSourceViewTypeDropdown_dataSources,
    type: 'datasource'
  },
  'dataconnection': {
    displayValue: Messages.dataSourceViewTypeDropdown_connections,
    type: 'dataconnection'
  }
};

export type DatasourceViewTypeButtonProps = ButtonProps & DatasourceViewTypeDropdownProps;

export interface DatasourceViewTypeDropdownProps {
  readonly viewType: DatasourceViewType;
  readonly viewTypeSelected: (viewType: DatasourceViewTypeInfo) => void;
  readonly disabled: boolean;
}

export class DatasourceViewTypeDropdown extends React.PureComponent<DatasourceViewTypeDropdownProps> {
  private memoizedButtonWrapper = defaultMemoize(getButtonWrapper);
  private memoizedChildPanelWrapper = defaultMemoize(getChildPanelWrapper);

  public constructor(props: DatasourceViewTypeDropdownProps) {
    super(props);
  }

  public render(): JSX.Element {
    const buttonComponent = DefaultButton;
    const buttonWrapper = this.memoizedButtonWrapper(buttonComponent, this.props);
    const panelWrapper = this.memoizedChildPanelWrapper(this.handleSelection, this.props);

    return <ButtonControlledComponent buttonComponent={buttonWrapper}
                                      childComponent={panelWrapper}
                                      testId={DatasourceViewTypeDropdownTestIds.dropDown}
                                      disabled={this.props.disabled} />;
  }

  private handleSelection = (value: string) => {
    this.props.viewTypeSelected(ViewTypeDictionary[value as DatasourceViewType]);
  }
}

const DefaultButton = (props: DatasourceViewTypeButtonProps): JSX.Element => {
  return (
    <ExplorerDropdownButton
      disabled={props.disabled}
      onClick={_.noop}
      testId={DatasourceViewTypeDropdownTestIds.button}
      title={''}
      renderButtonContent={() => {
        return ViewTypeDictionary[props.viewType].displayValue();
      }}
    />
  );
};

const getButtonWrapper = (
  ButtonComponent: React.ComponentType<DatasourceViewTypeButtonProps>,
  props: DatasourceViewTypeDropdownProps
): React.ComponentType<ButtonProps> => {
  return function(buttonProps: ButtonProps): JSX.Element {
    const fullProps = { ...buttonProps, ...props };
    return <ButtonComponent {...fullProps} />;
  };
};

const  getChildPanelWrapper = (
  handleSelection: (value: string) => void,
  props: DatasourceViewTypeDropdownProps
): React.ComponentType<ButtonControlledChildComponentProps> => {
  let menuItems = buildMenuItems(props);
  return function(props: ButtonControlledChildComponentProps): JSX.Element {
    const panelProps: ButtonControlledFlyoutListMenuProps = {
      onComponentDidMount: _.noop,
      menuItems: menuItems,
      onMenuItemSelected: handleSelection,
      anchorElement: props.anchorElement,
      closeMe: props.closeMe,
      testId: DatasourceViewTypeDropdownTestIds.childPanelWrapper
    };
    return <ButtonControlledFlyoutListMenu {...panelProps} />;
  };
};

const buildMenuItems = (props: DatasourceViewTypeDropdownProps): MenuItemInfo[] => {
  const items: MenuItemInfo[] = [];

  items.push({
    value: ViewTypeDictionary.datasource.type,
    displayValue: ViewTypeDictionary.datasource.displayValue(),
    disabled: false,
    checked: props.viewType === ViewTypeDictionary.datasource.type
  });

  items.push({
    value: ViewTypeDictionary.dataconnection.type,
    displayValue: ViewTypeDictionary.dataconnection.displayValue(),
    disabled: false,
    checked:  props.viewType === ViewTypeDictionary.dataconnection.type
  });

  return items;
};

export namespace DatasourceViewTypeDropdownTestIds {
  export const dropDown = 'content-explorer-ds-type-dropdown';
  export const button = 'content-explorer-ds-type-button';
  export const childPanelWrapper = 'content-explorer-ds-type-panel-wrapper';
}
